// Remote playback over a WebRTC DATA CHANNEL (design spec D8 / §8A.3 / §8A.6).
//
// The cloud-hosted https webapp cannot reach the device's same-LAN http endpoints
// (mixed-content + CORS + NAT), so REMOTE playback tunnels the §8A endpoints over a
// WebRTC data channel. We reuse the existing signaling for SDP/ICE relay via the NEW
// start_playback / stop_playback commands (mirror start_viewing/stop_viewing): an
// offer carrying m=application (SCTP data channel, NO media track) → device answer;
// server injects session_id + ice_servers(TURN); ICE flows over ice_candidate;
// stop_playback tears down.
//
// On the open data channel the client requests "/pb.m3u8?...", "/seg/<seq>.ts",
// "/snap/<ts>.jpg" and the device replies with the corresponding bytes; those bytes
// feed the HlsPlayer custom loader. hls.js does playlist parse + MSE + TS→fMP4
// transmux + seek over the tunneled bytes.
//
// Gated behind REC_FEAT_PLAYBACK_REMOTE. A real RTCPeerConnection needs a device, so
// the data-channel REQUEST/RESPONSE FRAMING is split out as a pure, mockable layer
// (PbTunnel over a DataChannelLike) and unit-tested against a fake channel.
//
// ---- Framing: "PB/1" request/response with chunked binary responses ----
//
// Request  (client→device, JSON text):
//   {"v":"PB/1","id":<n>,"op":"m3u8"|"seg"|"snap", ...params}
// Response header (device→client, JSON text):
//   {"v":"PB/1","id":<n>,"status":"ok"|"not_found"|"error","type":"text"|"binary",
//    "len":<bytes>,"chunks":<n>}   (chunks>1 ⇒ binary body split into ArrayBuffer frames)
// Response body:
//   - type:"text"   → the header MAY inline {"body":"<m3u8 text>"} (one frame), OR
//                     be followed by `chunks` binary frames (utf-8 decoded + joined).
//   - type:"binary" → `chunks` ArrayBuffer frames, concatenated in arrival order.
//
// This mirrors the device-side tunnel protocol in §8A.3 ("client sends {op,...},
// device replies with bytes; evict → not-found"). Keeping it a tiny framed RPC lets
// the browser correlate concurrent seg requests by id and reassemble chunked .ts.

import { signaling, type SignalFrame, type SignalingClient } from '../ws';

export const PB_PROTO = 'PB/1';

/** Minimal surface of an RTCDataChannel we depend on (so tests can mock it). */
export interface DataChannelLike {
  readyState: RTCDataChannelState;
  send(data: string): void;
  send(data: ArrayBuffer): void;
  onmessage: ((ev: { data: unknown }) => void) | null;
  onopen: ((ev?: unknown) => void) | null;
  onclose: ((ev?: unknown) => void) | null;
  close(): void;
}

/** A reassembled tunnel response. */
export interface TunnelResponse {
  status: 'ok' | 'not_found' | 'error';
  /** m3u8 text (op:m3u8) — undefined for binary responses. */
  text?: string;
  /** segment / snapshot bytes (op:seg / op:snap) — undefined for text responses. */
  bytes?: Uint8Array;
}

export const TUNNEL_TIMEOUT_MS = 30_000;

function toUint8(data: unknown): Uint8Array | null {
  if (data instanceof ArrayBuffer) return new Uint8Array(data);
  if (ArrayBuffer.isView(data)) return new Uint8Array(data.buffer, data.byteOffset, data.byteLength);
  return null;
}

function concat(parts: Uint8Array[], len: number): Uint8Array {
  const out = new Uint8Array(len);
  let off = 0;
  for (const p of parts) {
    out.set(p, off);
    off += p.length;
  }
  return out;
}

interface PbHeader {
  status: number;
  kind: string;
  total: number;
}

/** Parse the device header line "PB/1 <status> <kind> <total>\n" (pb_dc.c send_header). */
function parsePbHeader(text: string): PbHeader | null {
  if (!text.startsWith('PB/1 ')) return null;
  const m = text.trim().split(/\s+/); // ["PB/1", status, kind, total]
  if (m.length < 4) return null;
  const status = Number(m[1]);
  if (!Number.isFinite(status)) return null;
  const total = Number(m[3]);
  return { status, kind: m[2], total: Number.isFinite(total) ? total : 0 };
}

interface PbJob {
  path: string;
  resolve: (r: TunnelResponse) => void;
  reject: (e: Error) => void;
}

/**
 * PbTunnel — request/response over the device's §8A.3 data-channel wire protocol
 * (device pb_dc.c). The device protocol has NO request id, so requests are
 * SERIALIZED: one in flight, the rest queue (hls.js's loads thus go one at a time).
 *
 * Wire (each line = one data-channel message):
 *   request  (client→device): a plain request line, e.g. "/pb.m3u8?t1=..&t2=.." |
 *                             "/seg/<seq>.ts" | "/snap/<ts>.jpg"  (string message).
 *   response (device→client): a STRING header "PB/1 <status> <kind> <total>\n", then
 *                             zero+ BINARY chunks 'D'(0x44)+u32be(len)+payload, then a
 *                             STRING trailer "E\n".
 * status 200→ok, 404→not_found, else→error. kind "m3u8" → text; "ts"/"jpg" → bytes.
 */
export class PbTunnel {
  private readonly queue: PbJob[] = [];
  private active: { resolve: (r: TunnelResponse) => void; reject: (e: Error) => void; timer: ReturnType<typeof setTimeout> } | null = null;
  private header: PbHeader | null = null;
  private parts: Uint8Array[] = [];
  private readonly decoder = new TextDecoder();

  constructor(private readonly channel: DataChannelLike) {
    channel.onmessage = (ev) => this.onMessage(ev.data);
  }

  /** Queue a request for a tunnel URL (absolute or relative); resolves with the
   * reassembled response. Only the path+query is sent (the device parses the line). */
  requestUrl(url: string): Promise<TunnelResponse> {
    let path: string;
    try {
      const u = new URL(url, 'http://device.local');
      path = u.pathname + u.search;
    } catch {
      return Promise.reject(new Error(`bad tunnel url: ${url}`));
    }
    return new Promise<TunnelResponse>((resolve, reject) => {
      this.queue.push({ path, resolve, reject });
      this.pump();
    });
  }

  /** Reject everything in flight + queued and detach. Call on teardown. */
  destroy(): void {
    if (this.active) {
      clearTimeout(this.active.timer);
      this.active.reject(new Error('tunnel closed'));
      this.active = null;
    }
    for (const j of this.queue.splice(0)) j.reject(new Error('tunnel closed'));
    this.channel.onmessage = null;
  }

  /** Start the next queued request if the channel is idle + open. */
  private pump(): void {
    if (this.active || this.queue.length === 0) return;
    if (this.channel.readyState !== 'open') {
      for (const j of this.queue.splice(0)) j.reject(new Error('data channel not open'));
      return;
    }
    const job = this.queue.shift()!;
    this.header = null;
    this.parts = [];
    const timer = setTimeout(() => {
      if (this.active) {
        const a = this.active;
        this.active = null;
        a.reject(new Error(`tunnel request timed out: ${job.path}`));
        this.pump();
      }
    }, TUNNEL_TIMEOUT_MS);
    this.active = { resolve: job.resolve, reject: job.reject, timer };
    try {
      this.channel.send(job.path);
    } catch (e) {
      clearTimeout(timer);
      this.active = null;
      job.reject(e instanceof Error ? e : new Error('send failed'));
      this.pump();
    }
  }

  private onMessage(data: unknown): void {
    if (!this.active) return; // stray frame outside any request
    // The header ("PB/1 …") and trailer ("E\n") are sent as text on the device, but
    // the device→signaling→DC relay does not always preserve the text/binary flag,
    // so a header/trailer can land as EITHER a string or an ArrayBuffer. Discriminate
    // by the first byte instead: 'P'(0x50)=header, 'D'(0x44)=body chunk, 'E'(0x45)=end.
    const str = typeof data === 'string' ? data : null;
    const u8 = str === null ? toUint8(data) : null;
    const first = str !== null ? str.charCodeAt(0) : u8 && u8.length ? u8[0] : -1;

    if (first === 0x50 /* 'P' → PB/1 header */) {
      this.header = parsePbHeader(str ?? (u8 ? this.decoder.decode(u8) : ''));
      return;
    }
    if (first === 0x45 /* 'E' → trailer */) {
      this.finish();
      return;
    }
    if (first === 0x44 /* 'D' → body chunk */ && u8) {
      if (u8.length < 5) return;
      const len = ((u8[1] << 24) | (u8[2] << 16) | (u8[3] << 8) | u8[4]) >>> 0;
      this.parts.push(u8.subarray(5, 5 + len));
    }
  }

  /** Trailer seen → assemble the body + resolve the active request. */
  private finish(): void {
    if (!this.active) return;
    const a = this.active;
    this.active = null;
    clearTimeout(a.timer);
    const h = this.header;
    const status: TunnelResponse['status'] = !h ? 'error' : h.status === 200 ? 'ok' : h.status === 404 ? 'not_found' : 'error';
    const total = this.parts.reduce((n, p) => n + p.length, 0);
    const body = concat(this.parts, total);
    const resp: TunnelResponse =
      h && h.kind === 'm3u8' ? { status, text: this.decoder.decode(body) } : { status, bytes: body };
    this.header = null;
    this.parts = [];
    a.resolve(resp);
    this.pump();
  }
}

// ---- RTCPeerConnection session (production path; needs a device) ----

export interface RemotePlaybackSession {
  tunnel: PbTunnel;
  sessionId: string | null;
  stop: () => void;
}

interface StartGrant {
  answer: RTCSessionDescriptionInit;
  sessionId: string | null;
  iceServers: RTCIceServer[];
}

/** Pull {answer, session_id, ice_servers} off a start_playback response (mirror live). */
export function extractPlaybackGrant(resp: SignalFrame): StartGrant {
  const payload = (resp.payload as Record<string, unknown> | undefined) ?? {};
  const sdp = (payload.sdp ?? resp['sdp']) as RTCSessionDescriptionInit | undefined;
  const sessionId =
    (typeof resp.session_id === 'string' && resp.session_id) ||
    (typeof payload.session_id === 'string' && payload.session_id) ||
    null;
  const rawIce = (payload.ice_servers ?? resp['ice_servers']) as RTCIceServer[] | undefined;
  return {
    answer: sdp ?? { type: 'answer', sdp: '' },
    sessionId,
    iceServers: Array.isArray(rawIce) ? rawIce : [],
  };
}

/**
 * Open a remote playback session: create an m=application offer (data channel, no
 * media), send start_playback via ws.ts sendCmd for SDP/ICE relay, and resolve once
 * the data channel is open. The returned session exposes a PbTunnel for the custom
 * loader and a stop() that sends stop_playback + closes the peer connection.
 *
 * Real device required — covered by integration/e2e, not unit tests (the framing is
 * unit-tested via PbTunnel + a mocked DataChannelLike).
 */
export async function openRemotePlayback(
  deviceId: string,
  client: SignalingClient = signaling,
  pcFactory: () => RTCPeerConnection = () => new RTCPeerConnection()
): Promise<RemotePlaybackSession> {
  const pc = pcFactory();
  const channel = pc.createDataChannel('pb', { ordered: true });
  // Binary chunks (the 'D' frames) must arrive as ArrayBuffer, not Blob, so the
  // PbTunnel can read them synchronously.
  try {
    (channel as RTCDataChannel).binaryType = 'arraybuffer';
  } catch {
    /* mock channel in tests */
  }
  let sessionId: string | null = null;
  let stopped = false;

  const sendIce = (candidate: RTCIceCandidateInit) => {
    client.sendRaw({
      type: 'ice_candidate',
      session_id: sessionId ?? undefined,
      device_id: deviceId,
      candidate,
    } as SignalFrame);
  };
  pc.onicecandidate = (ev) => {
    if (ev.candidate && sessionId) sendIce(ev.candidate.toJSON());
  };

  // Add the device's trickled ICE candidates (relayed as ice_candidate frames). Without
  // this only host/srflx candidates inline in the answer would work — cross-NAT (cloud
  // webapp ↔ device behind NAT) needs the trickle, so a missing handler = ICE never
  // completes. Candidates that arrive before setRemoteDescription are buffered + flushed.
  const pendingRemote: RTCIceCandidateInit[] = [];
  let remoteReady = false;
  const addRemote = (cand: RTCIceCandidateInit) => {
    if (remoteReady) pc.addIceCandidate(cand).catch(() => {});
    else pendingRemote.push(cand);
  };
  const offIce = client.on('ice_candidate', (frame: SignalFrame) => {
    const f = frame as Record<string, unknown>;
    const payload = (f.payload as Record<string, unknown> | undefined) ?? {};
    const fsid =
      (typeof f.session_id === 'string' && f.session_id) ||
      (typeof payload.session_id === 'string' && payload.session_id) ||
      null;
    if (sessionId && fsid && fsid !== sessionId) return; // not our session
    const cand = (f.candidate ?? payload.candidate) as RTCIceCandidateInit | undefined;
    if (cand) addRemote(cand);
  });

  // On any failure before the session is returned, unsubscribe + close the PC so the
  // ice_candidate listener and RTCPeerConnection don't leak.
  let tunnel: PbTunnel;
  try {
    const offer = await pc.createOffer();
    const resp = await client.request('start_playback', { sdp: offer }, { device_id: deviceId });
    const grant = extractPlaybackGrant(resp);
    sessionId = grant.sessionId;
    if (grant.iceServers.length > 0) {
      try {
        pc.setConfiguration({ iceServers: grant.iceServers });
      } catch {
        /* host/srflx fallback */
      }
    }
    await pc.setLocalDescription(offer);
    await pc.setRemoteDescription(grant.answer);
    remoteReady = true;
    for (const c of pendingRemote.splice(0)) pc.addIceCandidate(c).catch(() => {});

    tunnel = new PbTunnel(channel as unknown as DataChannelLike);
    await new Promise<void>((resolve, reject) => {
      if (channel.readyState === 'open') return resolve();
      const timer = setTimeout(() => reject(new Error('data channel open timeout')), TUNNEL_TIMEOUT_MS);
      channel.onopen = () => {
        clearTimeout(timer);
        resolve();
      };
      channel.onclose = () => {
        clearTimeout(timer);
        reject(new Error('data channel closed before open'));
      };
    });
  } catch (e) {
    offIce();
    try {
      pc.close();
    } catch {
      /* already closed */
    }
    throw e;
  }

  const stop = () => {
    if (stopped) return;
    stopped = true;
    offIce();
    tunnel.destroy();
    if (sessionId) {
      client.request('stop_playback', { session_id: sessionId }, { device_id: deviceId }).catch(() => {});
    }
    try {
      pc.close();
    } catch {
      /* closed */
    }
  };

  return { tunnel, sessionId, stop };
}
